import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";
import { zeroNetPrice } from "~/utils/PriceUtils";
import { SearchPageType } from "~/Search/useSearchPageType";

const performanceCookies = "C0002";

function isGroupEnabled(code: string) {
  return window.OnetrustActiveGroups?.indexOf(code) !== -1;
}

function loadScript(src: string, onCb: (() => void) | undefined = undefined, async = true, defer = false) {
  const script = document.createElement("script");
  script.src = src;
  script.defer = defer;
  if (onCb) {
    script.onload = onCb;
  }
  script.async = async;
  document.head.appendChild(script);
}

export async function startZoovuTracking() {
  await window._bannerInteractedPromise;

  window.zoovuSearchTrackingConfiguration = {
    projectId: window.SCHA.config.zoovuProjectId, // your project ID
    baseUrl: "https://api.search.zoovu.com/insights",
    interface: {
      searchBox: {
        selector: "#searchBoxInput",
        button: "#searchBoxSubmit",
        initializationStrategy: {
          type: "interval",
          waitForElem: "#searchBoxInput",
          duration: 250,
        },
      },
      searchSuggestions: {
        blockSelector: "[data-zoovu=searchSuggestions]",
        itemSelector: "[data-zoovu=searchSuggestion]",
        activeItemSelector: "[data-zoovu=searchSuggestion]:has(*:hover)",
        initializationStrategy: {
          type: "observer",
          wrapper: ".scha__search-box",
        },
        articleNumberSource: {
          type: "domElement",
          selector: "[data-prod-article]",
          source: "@data-prod-article",
        },
        identifierSource: {
          type: "domElement",
          selector: "[tracking-aux-sch-prod-name]",
          source: "@tracking-aux-sch-prod-name",
        },
      },
      hasCheckoutTracking: true,
      allowCookies: isGroupEnabled(performanceCookies),
      debug: true, // whether or not to output debugging information on the dev console
    },
  };

  loadScript("https://cdn.search.zoovu.com/zoovu-tracker.min.js", undefined, false, true);

  // sku = hybris code
  // Link = product.url
  // Count = quantity
  // Price unit = price.currencyIso
  // Unit price = price.value / price.unit

  AnalyticsMQ.instance.subscribe("addToCart", ({ products, context: { source } }) => {
    if (!["srp", "autocomplete", "pdp"].includes(source)) {
      // Other sources are not tracked by Zoovu
      return;
    }
    (Array.isArray(products) ? products : [products]).forEach(
      ({ product, price = zeroNetPrice(product), quantity }) => {
        window.ZoovuSearchInsightsTracker?.trackAddToCart(
          product.hybrisCode,
          product.hybrisCode,
          product.url,
          quantity,
          price.value / price.unit,
          price.currencyIso,
          source === "srp",
          source === "autocomplete",
        );
      },
    );
  });

  AnalyticsMQ.instance.subscribe("placeOrder", ({ entries }) => {
    // All sources are tracked by zoovu
    window.ZoovuSearchInsightsTracker?.trackBuy(
      entries.map(({ lineItem, product }) => {
        const price = lineItem.netPrice ?? zeroNetPrice(product);
        const unitPrice = price.value / price.unit;
        const priceUnit = price.currencyIso;
        return {
          articleNumber: product.base.hybrisCode,
          identifier: product.base.designation,
          link: product.base.url,
          count: lineItem.quantity,
          unitPrice,
          priceUnit,
        };
      }),
    );
  });
}

AnalyticsMQ.instance.subscribe("searchPageOpen", async () => {
  await window._bannerInteractedPromise;
  if (!isGroupEnabled(performanceCookies)) {
    return;
  }

  let queryHolder = "";
  let resultAmountHolder = 0;
  AnalyticsMQ.instance.subscribe("searchSuccess", ({ query, resultAmount, pageNumber, webpageType, pageSize }) => {
    if (webpageType !== SearchPageType.SEARCH) {
      return;
    }
    if (pageNumber !== 0) {
      return;
    }
    queryHolder = query;
    resultAmountHolder = pageSize;
    window.ZoovuSearchInsightsTracker?.trackSerpShow(undefined, undefined, query, resultAmount);
  });

  window.addEventListener("beforeunload", () => {
    const el = window.document.activeElement;
    if (el?.tagName.toLowerCase() === "a") {
      const link = el as HTMLAnchorElement;
      const isNavigateToProductDetails = (link.href.toLowerCase() ?? "").includes("/p/");
      if (isNavigateToProductDetails) {
        const productTile = link.closest(".jsSearchResultProduct");
        if (!productTile) {
          return;
        }
        window.ZoovuSearchInsightsTracker?.trackSerpClick(
          queryHolder,
          link,
          undefined,
          resultAmountHolder,
          +productTile.getAttribute("data-prod-position")!,
          0,
          link.href,
          undefined,
          productTile.getAttribute("data-prod-article")!,
          productTile.getAttribute("data-prod-article")!,
        );
      } else {
        window.ZoovuSearchInsightsTracker?.trackSerpLeave(undefined, undefined, queryHolder, resultAmountHolder);
      }
    }
  });
});
